import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ChevronRight,
  Globe,
  Mail,
  MailWarning,
  Pencil,
  ShieldCheck,
  UserPlus,
  Users,
} from "lucide-react";
import { type ComponentType, type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client.js";
import { useSession } from "../../auth/session.js";
import { Button, ErrorNotice, Input } from "../../components/form.js";

type Icon = ComponentType<{ className?: string }>;

/** First screen of the console, like Google Admin's home: the numbers, then where to go next. */
export function AdminOverviewPage() {
  const { t } = useTranslation();
  const session = useSession();
  const users = useQuery({ queryKey: ["users", "list"], queryFn: () => api.users.list() });
  const invites = useQuery({ queryKey: ["invites", "list"], queryFn: () => api.invites.list() });
  const domains = useQuery({ queryKey: ["domains", "owned"], queryFn: () => api.domains.owned() });
  const mailboxes = useQuery({ queryKey: ["mailboxes"], queryFn: () => api.mailboxes.list() });

  const members = users.data?.users ?? [];
  const withMfa = members.filter((u) => u.mfaEnabled).length;
  const unverified = members.filter((u) => !u.emailVerified).length;
  const count = (ready: boolean, value: number | string) => (ready ? String(value) : "…");

  const tiles: Array<{ label: string; value: string; icon: Icon; to: string; tone: string }> = [
    {
      label: t("admin.overview.members"),
      value: count(Boolean(users.data), members.length),
      icon: Users,
      to: "/admin/users",
      tone: "bg-[#d3e3fd] text-[#0b3a82]",
    },
    {
      label: t("admin.overview.mailboxes"),
      value: count(Boolean(mailboxes.data), mailboxes.data?.mailboxes.length ?? 0),
      icon: Mail,
      to: "/admin/users",
      tone: "bg-accent-soft text-accent",
    },
    {
      label: t("admin.overview.domains"),
      value: count(Boolean(domains.data), domains.data?.domains.length ?? 0),
      icon: Globe,
      to: "/admin/domains",
      tone: "bg-[#e8def8] text-[#3b2a6b]",
    },
    {
      label: t("admin.overview.mfa"),
      value: users.data ? `${withMfa} / ${members.length}` : "…",
      icon: ShieldCheck,
      to: "/admin/users",
      tone: "bg-[#c4eed0] text-[#0f5223]",
    },
  ];
  const steps: Array<{ label: string; hint: string; icon: Icon; to: string }> = [
    {
      label: t("admin.overview.inviteLink"),
      hint: t("admin.overview.pendingCount", { count: invites.data?.invites.length ?? 0 }),
      icon: UserPlus,
      to: "/admin/users",
    },
    {
      label: t("admin.overview.domainLink"),
      hint: t("admin.overview.domainHint"),
      icon: Globe,
      to: "/admin/domains",
    },
    {
      label: t("admin.overview.securityLink"),
      hint: t("admin.overview.securityHint"),
      icon: ShieldCheck,
      to: "/settings/security",
    },
  ];

  return (
    <section className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-4 md:p-8">
      <div>
        <WorkspaceName name={session.data?.tenant.name ?? ""} />
        <p className="mt-1 text-sm text-ink-2">
          {t("admin.nav.overview")} ·{" "}
          {session.data ? t(`admin.tenantStatus.${session.data.tenant.status}`) : ""}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {tiles.map((tile) => (
          <Link
            key={tile.label}
            to={tile.to}
            className="flex flex-col gap-3 rounded-2xl border border-rule bg-surface p-4 hover:shadow-1"
          >
            <span className={`flex size-10 items-center justify-center rounded-full ${tile.tone}`}>
              <tile.icon className="size-5" />
            </span>
            <span>
              <span className="block text-[28px] leading-none text-ink tabular-nums">
                {tile.value}
              </span>
              <span className="mt-1 block text-sm text-ink-2">{tile.label}</span>
            </span>
          </Link>
        ))}
      </div>

      {users.data && unverified > 0 ? (
        <Link
          to="/admin/users"
          className="flex items-center gap-3 rounded-2xl bg-warn-soft px-4 py-3 text-sm text-warn"
        >
          <MailWarning className="size-5 shrink-0" />
          <span className="flex-1">
            {t("admin.overview.unverifiedNote", { count: unverified })}
          </span>
          <ChevronRight className="size-4" />
        </Link>
      ) : null}

      <section className="rounded-2xl border border-rule bg-surface">
        <h2 className="px-5 pt-5 pb-2 text-base font-medium text-ink">
          {t("admin.overview.nextSteps")}
        </h2>
        <ul className="divide-y divide-rule">
          {steps.map((step) => (
            <li key={step.label}>
              <Link to={step.to} className="flex items-center gap-4 px-5 py-4 hover:bg-ink/[0.03]">
                <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-surface-2 text-ink-2">
                  <step.icon className="size-5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm text-ink">{step.label}</span>
                  <span className="block text-xs text-ink-3">{step.hint}</span>
                </span>
                <ChevronRight className="size-5 text-ink-3" />
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </section>
  );
}

/** The workspace's name, renamed in place. */
function WorkspaceName({ name }: { name: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);
  const rename = useMutation({
    mutationFn: () => api.workspace.rename(value),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
      setEditing(false);
    },
  });
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    rename.mutate();
  };
  if (!editing) {
    return (
      <div className="flex items-center gap-2">
        <h1 className="min-w-0 truncate text-[28px] leading-tight text-ink">{name}</h1>
        <button
          type="button"
          aria-label={t("admin.overview.rename")}
          title={t("admin.overview.rename")}
          className="flex size-9 shrink-0 items-center justify-center rounded-full text-ink-3 hover:bg-ink/5 hover:text-ink"
          onClick={() => {
            setValue(name);
            setEditing(true);
          }}
        >
          <Pencil className="size-4" />
        </button>
      </div>
    );
  }
  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-2 sm:flex-row sm:items-center">
      <div className="sm:w-80">
        <Input
          aria-label={t("admin.overview.rename")}
          required
          maxLength={200}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoFocus
        />
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={rename.isPending || !value.trim()}>
          {t("common.save")}
        </Button>
        <Button variant="secondary" onClick={() => setEditing(false)}>
          {t("common.cancel")}
        </Button>
      </div>
      <ErrorNotice error={rename.error} />
    </form>
  );
}
